"use client";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import Logo from "../../../../public/assets/logo.png";
import { usePathname } from "next/navigation";
import { BiChevronDown } from "react-icons/bi";
import { MenuItems, MenuItems2 } from "../MenuItem";
import { MdOutlineKeyboardDoubleArrowUp } from "react-icons/md";

const DesktopHeader = () => {
  const pathname = usePathname();
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScroll, setShowScroll] = useState(false);

  const handleMouseEnter = (id: number) => setOpenDropdownId(id);
  const handleMouseLeave = () => setOpenDropdownId(null);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => setShowScroll(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const renderLink = (href: string, children: React.ReactNode, className = "") => {
    const isExternal = href.startsWith("http");
    if (isExternal) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {children}
        </a>
      );
    }
    return (
      <Link href={href} className={className}>
        {children}
      </Link>
    );
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 border-b px-5 ${
          isScrolled
            ? "bg-white shadow-md border-gray-200"
            : "bg-white border-gray-50/20"
        }`}
      >
        <div className="container mx-auto grid grid-cols-3 gap-2 py-2">
          {/* Left Menu */}
          <ul className="flex gap-8 items-center relative">
            {MenuItems.map((item) => (
              <li
                key={item.id}
                className="relative group"
                onMouseEnter={() => handleMouseEnter(+item.id)}
                onMouseLeave={handleMouseLeave}
              >
                {renderLink(
                  item.href ?? "/error",
                  <>
                    {item.name}
                    {item.submenu && <BiChevronDown size={20} />}
                  </>,
                  `flex items-center text-[#282930] gap-1 text-sm font-semibold transition ${
                    pathname === item.href
                      ? "!text-red-400"
                      : isScrolled
                      ? "text-[#282930] hover:text-primary"
                      : "hover:text-primary"
                  }`
                )}

                {item.submenu && (
                  <ul
                    className={`absolute left-0 top-[65%] w-[250px] mt-2 bg-white shadow-md rounded flex flex-col gap-2 z-40 py-3 transition-all duration-300 origin-top transform ${
                      openDropdownId === +item.id
                        ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                    }`}
                  >
                    {item.submenu.map((sub) => (
                      <React.Fragment key={`submenu-${sub.id}`}>
                        <li className="hover:text-blue-600 font-normal px-4 group">
                          {renderLink(
                            sub.href,
                            sub.name,
                            "text-sm w-full block"
                          )}
                        </li>
                        <li className="last:hidden opacity-20">
                          <hr />
                        </li>
                      </React.Fragment>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>

          {/* Logo */}
          <div className="flex justify-center">
            <Link href="/" className="w-[45%] ">
              <Image
                src={Logo}
                alt="logo"
                className={`w-full transition-all duration-300 ${
                  isScrolled ? "scale-90" : "scale-100"
                }`}
              />
            </Link>
          </div>

          {/* Right Menu */}
          <ul className="flex gap-8 items-center justify-end relative">
            {MenuItems2.map((item, index) => {
              const isLast = index >= MenuItems2.length - 2;
              return (
                <li
                  key={item.id}
                  className="relative group"
                  onMouseEnter={() => handleMouseEnter(+item.id)}
                  onMouseLeave={handleMouseLeave}
                >
                  {renderLink(
                    item.href ?? "/error",
                    <>
                      {item.name}
                      {item.submenu && <BiChevronDown size={20} />}
                    </>,
                    `flex items-center text-[#282930] gap-1 text-sm font-semibold transition ${
                      pathname === item.href
                        ? "!text-red-400"
                        : isScrolled
                        ? "text-[#282930] hover:text-primary"
                        : "hover:text-primary"
                    }`
                  )}

                  {item.submenu && (
                    <ul
                      className={`absolute ${
                        isLast ? "right-0" : "left-0"
                      } top-[65%] w-[250px] mt-2 bg-white shadow-md rounded flex flex-col gap-2 z-40 py-3 transition-all duration-300 origin-top transform ${
                        openDropdownId === +item.id
                          ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
                          : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                      }`}
                    >
                      {item.submenu.map((sub) => (
                        <React.Fragment key={`submenu2-${sub.id}`}>
                          <li className="hover:text-blue-600 font-normal px-4 group">
                            {renderLink(
                              sub.href,
                              sub.name,
                              "text-sm w-full block"
                            )}
                          </li>
                          <li className="last:hidden opacity-20">
                            <hr />
                          </li>
                        </React.Fragment>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* Scroll-to-top Button */}
      <button
        onClick={scrollToTop}
        className={`
          fixed xl:bottom-6 bottom-10 hidden right-6 p-3 cursor-pointer rounded-full bg-white text-red-500 
          transition-all duration-500 ease-in-out transform shadow-2xl border-2 border-red-400
          ${
            showScroll
              ? "opacity-100 scale-100"

              : "opacity-0 scale-75 pointer-events-none"
          }
          hover:bg-red-600 hover:text-white
        `}
        aria-label="Scroll to top"
      >
        <MdOutlineKeyboardDoubleArrowUp size={22} />
      </button>
    </>
  );
};

export default DesktopHeader;
